import { Typography } from '@safe-global/views/components/ui/typography'
import { Spinner } from '@safe-global/views/components/ui/spinner'
import { useEffect, useRef, useState } from 'react'
import { trackEvent, HYPERNATIVE_EVENTS } from '@/services/analytics'

type HubSpotFormProps = {
  portalId: string
  formId: string
  region?: string
  onSubmit?: (region: string) => void
}

const HubSpotForm = ({ portalId, formId, region = 'eu1', onSubmit }: HubSpotFormProps) => {
  const formContainerRef = useRef<HTMLDivElement>(null)
  const scriptLoadedRef = useRef(false)
  const selectedRegionRef = useRef<string>('AMERICAS')
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (scriptLoadedRef.current) {
      return
    }

    const script = document.createElement('script')
    script.src = 'https://js-eu1.hsforms.net/forms/embed/v2.js'
    script.charset = 'utf-8'
    script.type = 'text/javascript'
    script.async = true

    script.onload = () => {
      if (window.hbspt && formContainerRef.current) {
        window.hbspt.forms.create({
          portalId,
          formId,
          region,
          target: `#${formContainerRef.current.id}`,
          inlineMessage: '', // Prevent HubSpot's default inline thank you message
          redirectUrl: '', // Prevent HubSpot's redirect
          onFormReady: (form: HTMLFormElement) => {
            try {
              setIsLoading(false)
              if (form.elements) {
                const urlField = form.elements.namedItem('conversion_page_url') as HTMLInputElement
                if (urlField) {
                  urlField.value = window.location.href
                  const changeEvent = new Event('change', { bubbles: true })
                  urlField.dispatchEvent(changeEvent)
                }
              }

              // Track region field changes and default to AMERICAS when empty
              const regionField = form.elements.namedItem('region') as HTMLSelectElement
              if (regionField) {
                const initialValue = regionField.value
                if (initialValue) {
                  selectedRegionRef.current = String(initialValue).toUpperCase()
                } else {
                  const defaultRegion = 'AMERICAS'
                  const option = Array.from(regionField.options).find(
                    (opt) =>
                      String(opt.value).toUpperCase() === defaultRegion ||
                      String(opt.text).toUpperCase().trim() === defaultRegion,
                  )
                  if (option) {
                    regionField.value = option.value
                    const changeEvent = new Event('change', { bubbles: true })
                    regionField.dispatchEvent(changeEvent)
                    selectedRegionRef.current = defaultRegion
                  }
                }
                regionField.addEventListener('change', (e) => {
                  selectedRegionRef.current = String((e.target as HTMLSelectElement).value || '').toUpperCase()
                })
              }
            } catch (error) {
              console.warn('[HubSpotForm] Error in onFormReady:', error)
            }
          },
          onFormSubmitted: ($form: any, data: any) => {
            trackEvent(HYPERNATIVE_EVENTS.GUARDIAN_FORM_SUBMITTED)

            if (data) {
              let regionValue: string | undefined

              if (Array.isArray(data)) {
                const regionField = data.find((field: any) => field.name === 'region')
                regionValue = regionField?.value
              } else if (typeof data === 'object') {
                if (data.submissionValues) {
                  if (Array.isArray(data.submissionValues)) {
                    const regionField = data.submissionValues.find((field: any) => field.name === 'region')
                    regionValue = regionField?.value
                  } else if (typeof data.submissionValues === 'object') {
                    regionValue = data.submissionValues.region
                  }
                } else {
                  regionValue = data.region || data.region_of_operation
                }
              }

              if (regionValue) {
                selectedRegionRef.current = String(regionValue).toUpperCase()
              }
            }

            onSubmit?.(selectedRegionRef.current)
          },
        })
      }
    }

    document.body.appendChild(script)
    scriptLoadedRef.current = true

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script)
      }
      scriptLoadedRef.current = false
    }
  }, [portalId, formId, region])

  return (
    <div className="min-h-full bg-[var(--color-static-primary)] py-2">
      <Typography variant="h3" className="mb-2 text-[var(--color-static-main)]">
        Request demo
      </Typography>
      <Typography variant="paragraph" className="mb-8 text-[var(--color-static-light)]">
        Share your details to book a demo call.
      </Typography>
      {isLoading && (
        <div className="flex min-h-[400px] items-center justify-center">
          <Spinner className="size-10 text-[var(--color-static-main)]" />
        </div>
      )}
      <div id="hubspot-form-container" ref={formContainerRef} style={{ display: isLoading ? 'none' : 'block' }} />
    </div>
  )
}

export default HubSpotForm
